import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function NewsAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Global - News API" description="Query global data via the News API; request parameters, code examples and the response schema for the global endpoint."  canonicalUrl="https://axionquant.com/docs/04-macros-and-signals/news/global" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Global - News API</DocHeading>
      <div className="space-y-12" ref={sectionRef}>
        
        {/* Get News by Country */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /news/country/:country
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves news articles filtered by country. This endpoint provides localized news coverage, making it ideal for tracking regional developments, local market news, and country-specific economic reports.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">country</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Country name
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <Links links={['/news/country/US', '/news/country/GB']} />
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Available Countries</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: "america",   label: "United States",        flag: "🇺🇸" },
                  { value: "uk",        label: "United Kingdom",       flag: "🇬🇧" },
                  { value: "canada",    label: "Canada",               flag: "🇨🇦" },
                  { value: "china",     label: "China",                flag: "🇨🇳" },
                  { value: "hongkong",  label: "Hong Kong",            flag: "🇭🇰" },
                  { value: "germany",   label: "Germany",              flag: "🇩🇪" },
                  { value: "india",     label: "India",                flag: "🇮🇳" },
                  { value: "japan",     label: "Japan",                flag: "🇯🇵" },
                  { value: "singapore", label: "Singapore",            flag: "🇸🇬" },
                  { value: "australia", label: "Australia",            flag: "🇦🇺" },
                  { value: "korea",     label: "South Korea",          flag: "🇰🇷" },
                  { value: "ksa",       label: "Kingdom of Saudi Arabia", flag: "🇸🇦" },
                  { value: "mexico",    label: "Mexico",               flag: "🇲🇽" },
                ].map(({ value, label, flag }) => (
                    <div key={value} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                      <span className="text-xl leading-none">{flag}</span>
                      <div className="min-w-0">
                        <code className="text-xs font-mono text-blue-700">{value}</code>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <p className="text-sm text-gray-600 mb-3">Same structure as Company News response.</p>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">title <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Article headline</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">link <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Encoded URL to the full article</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">summary <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Article summary or excerpt</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">published <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Publication date (RFC 1123 format)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="News by Country" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">News by Country</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="news/country"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="04-macros-and-signals/news/global"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
